import * as React from "react";
import { merge, $, media } from "glamor";
import { theme } from "styles/theme";
import IconButton from "material-ui/IconButton";
import { formatMessage } from "lib/globalize-util";
import { Map, fromJS } from "immutable";
import { ChatIcon } from "components/icons/assortments/chat";
import { View, retailKnowledgeBaseUrl } from "constants/product-collection";
import { DATATEST } from "constants/integration";
import classNames from "classnames";
import CartIcon from "components/icons/assortments/cart";
import ShareIcon from "material-ui/svg-icons/social/person-add";
import { Avatar } from "./product-collection-header-components/avatar";
import avatarInitials from "lib/avatar-initials";
import Badge from "material-ui/Badge";
import { CollectionPOI } from "records/collection";
import { Scope } from "./product-collection-header-components/scope";
import moment from "moment";
import { Back as BackIcon } from "components/icons/back";
import NuOrderIcon from "components/icons/nuorder";
import FlatButton from "material-ui/FlatButton";
import Caret from "components/icons/caret";
import Popover, { PopoverAnimationVertical } from "material-ui/Popover";
import MenuItem from "material-ui/MenuItem";
import Divider from "material-ui/Divider";
import MoreIcon from "material-ui/svg-icons/navigation/more-horiz";
import CloseIcon from "material-ui/svg-icons/navigation/close";
import Tooltip from "@material-ui/core/Tooltip";
import { Link } from "react-router";
import { DRIVE_LINK } from "containers/folders/utils/constants";
import { ItemKind } from "containers/folders/types/base";
import { nameExtRemover } from "containers/folders/utils/space-item";
import { ProductCollectionStatusPill } from "./common/product-collection-status-pill";
import { AssortmentBreadcrumb } from "containers/folders/containers/space-manager/components/folder-selection/assortment-breadcrumb";
import { RollupType } from "containers/folders/types/server-rollups";
import { IFetchBrandsDataPayload } from "actions/brands-data-actions";
import { IBrandsDataStatePayload } from "reducers/brands-data-reducers";
import { CollectionType } from "containers/folders/types/assortment";
import { usePrevious } from "hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import { hasVerticalAssortmentAccess } from "containers/folders/utils/vertical-assortments";
import { SearchSettings } from "constants/types";

const MAX_LENGTH = 64;

interface IProps {
    me: any;
    collection?: any;
    capabilities: nuorder.folder.v1.Capabilities;
    collectionDriveItem: nuorder.folder.v1.Item;
    collectionId?: string;
    activeUserSessions: Map<string, any>;
    rowDataSet: boolean;
    location: any;
    params: any;
    poiEntities?: { [collectionPoiId: string]: CollectionPOI };
    onSubscribeToItemsUpdates: () => void;
    router: any;
    brandsDataById: IBrandsDataStatePayload["brandsDataById"];
    onToggleChat: (on?: boolean) => void;
    showChat: boolean;
    chatState: any;
    onToggleShare: () => void;
    onUpdateCollectionAttribute: (id, prop, value, reset?) => void;
    isNew?: boolean;
    isCollectionSettingsOpen?: boolean;
    isRollupMode?: boolean;
    rollupType?: RollupType;
    versionStatusLabel?: string;
    onFetchBrandsData: (query: IFetchBrandsDataPayload["query"]) => void;
    isFetchingBrands: boolean;
    userMerchantId: string;
    brand?: any;
}

export const ProductCollectionHeader: React.FunctionComponent<IProps> = ({
    me,
    capabilities,
    activeUserSessions,
    location,
    showChat,
    brandsDataById,
    chatState,
    router,
    onToggleChat,
    onToggleShare,
    collection,
    collectionId,
    poiEntities,
    onUpdateCollectionAttribute,
    isNew,
    isCollectionSettingsOpen,
    onSubscribeToItemsUpdates,
    params,
    collectionDriveItem,
    isRollupMode,
    rollupType,
    versionStatusLabel,
    onFetchBrandsData,
    isFetchingBrands,
    userMerchantId,
    brand,
}) => {
    const { assortmentSettings, verticalAssortmentsEnabled } = useFlags();

    const isVerticalAssortmentEnabled = React.useMemo(
        () => !!brand && hasVerticalAssortmentAccess(me, brand, verticalAssortmentsEnabled),
        [brand, verticalAssortmentsEnabled, me]
    );

    const styles = React.useMemo(
        () =>
            merge(
                {
                    backgroundColor: isRollupMode ? theme.palette.rollupHeaderBackgound : theme.palette.darkestGrey,
                    color: "rgba(255, 255, 255, .8)",
                    boxShadow: "rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px",
                    zIndex: 1,
                    display: "flex",
                    height: "60px",
                    maxHeight: "60px",
                    alignItems: "center",
                    position: "relative",
                    paddingRight: "4px",
                },
                $(" .appMenu", {
                    display: "flex",
                    height: "24px",
                    maxHeight: "24px",
                    alignItems: "center",
                    position: "relative",
                    padding: "0 8px",
                }),
                $(" .historyHeader", {
                    background: "white",
                    padding: "8px 16px",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    zIndex: "1000",
                    display: "flex",
                    alignItems: "center",
                }),
                $(" .historyHeader .description", {
                    flexGrow: 1,
                    color: theme.palette.darkGrey,
                }),
                $(" .historyHeader .title", {
                    fontSize: "16px",
                    lineHeight: "24px",
                    paddingTop: "0px",
                    color: theme.palette.darkestGrey,
                }),
                $(" .historyHeader .scopeContainer", {
                    flexGrow: 4,
                    display: "inline-block",
                }),
                $(" .historyHeader .scopeInnerContainer", {
                    display: "inline-block",
                }),
                $(" .historyHeader .scope", {
                    backgroundColor: `${theme.palette.darkGrey} !important`,
                }),
                $(" .rightPanel", {
                    alignItems: "center",
                    display: "flex",
                    height: "100%",
                    justifyContent: "flex-end",
                    paddingRight: "8px",
                }),
                $(" .lastEdited", {
                    display: "inline-block",
                    paddingLeft: "50px",
                }),
                $(" .assortmentTitleContainer", {
                    maxWidth: "240px",
                    paddingRight: "8px",
                }),
                $(" .assortmentTitleContainer.rollupMode", {
                    maxWidth: "580px",
                    width: "100%",
                }),
                $(" .assortmentTitle", {
                    textAlign: "left",
                    fontSize: "18px",
                    width: "100%",
                    lineHeight: "22px",
                    minWidth: "20px",
                    padding: "0 4px",
                    border: "1px solid transparent",
                    borderRadius: "4px",
                    textOverflow: "ellipsis",
                    background: "transparent",
                    color: "rgba(255, 255, 255, .94)",
                }),
                $(" .assortmentStatus", {
                    minWidth: "95px",
                    fontSize: "14px",
                    padding: "0 5px",
                    marginRight: "3rem",
                    alignSelf: "flex-end",
                    marginBottom: "13px",
                }),
                $(" input.assortmentTitle:hover", {
                    borderColor: "rgba(255, 255, 255, .35)",
                    backgroundColor: "rgba(255, 255, 255, .2)",
                }),
                $(" input.assortmentTitle:focus", {
                    borderColor: "rgba(255, 255, 255, .35)",
                    backgroundColor: "rgba(255, 255, 255, .2)",
                }),
                $(" .assortmentTitle:disabled", {
                    backgroundColor: "inherit",
                }),
                $(" .assortmentBreadcrumbsWrapper", {
                    minHeight: "16px",
                    lineHeight: "16px",
                }),
                $(" .assortmentTitleBreadcrumbs", {
                    lineHeight: "16px",
                    paddingLeft: "4px",
                }),
                $(" .assortmentTitleBreadcrumbs a", {
                    color: "rgba(255, 255, 255, .8)",
                    textDecoration: "underline",
                    display: "inline-block",
                    margin: "0 4px 0 4px",
                }),
                $(" .assortmentTitleBreadcrumbs a:hover", {
                    color: "white",
                }),
                $(" .assortmentTitleBreadcrumbs span", {
                    color: "rgba(255, 255, 255, .5)",
                    padding: "0 4px",
                }),
                $(" .assortmentHeaderTotal", {
                    lineHeight: "24px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    paddingLeft: "12px",
                    paddingRight: "8px",
                }),
                $(" .flexItemWide", {
                    flexGrow: 1,
                    flexShrink: 1,
                    height: "100%",
                    display: "flex",
                    minWidth: "0",
                    padding: "0 16px 0 0",
                    alignItems: "center",
                }),
                $(" .flexItemWide .iconButton svg", {
                    color: "rgba(255, 255, 255, .8) !important",
                }),
                $(" .flexItemWide .iconButton:hover svg", {
                    color: "rgba(255, 255, 255, 1) !important",
                }),
                $(" .cartButton svg", {
                    color: "rgba(255, 255, 255, .8) !important",
                }),
                $(" .cartButton:hover svg", {
                    color: "rgba(255, 255, 255, 1) !important",
                }),
                $(" .profileButton", {
                    color: "rgba(255, 255, 255, .8) !important",
                }),
                $(" .profileButton span", {
                    maxWidth: "100px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "inline-block",
                }),
                $(" .profileButton:hover", {
                    color: "rgba(255, 255, 255, 1) !important",
                }),
                $(" .profileButton svg", {
                    fill: "rgba(255, 255, 255, .8) !important",
                }),
                $(" .profileButton:hover svg", {
                    fill: "rgba(255, 255, 255, 1) !important",
                }),
                $(" .appIcon", {
                    height: "100%",
                    width: "60px",
                    position: "relative",
                    display: "block",
                }),
                $(" .appIcon svg", {
                    height: "36px !important",
                    width: "36px !important",
                    color: "#82b4e4 !important",
                    top: "50%",
                    left: "16px",
                    position: "absolute",
                    transform: "translate3d(0, -50%, 0)",
                }),
                $(" .hasAddButton", {
                    flexShrink: 0,
                }),
                $(" .mainContent", {
                    alignItems: "center",
                    display: "flex",
                    flexGrow: 1,
                    height: "100%",
                    justifyContent: "space-between",
                }),
                $(" .actionsContainer", {
                    flexBasis: "60px",
                }),
                $(" .flexItemLeft", {
                    display: "flex",
                    height: "60px",
                }),
                $(" .chatBadge", {
                    padding: "0px !important",
                }),
                $(" .chatBadge > span", {
                    opacity: 0,
                    transition: "all .2s ease-in-out",
                }),
                $(" .chatBadge.hasUnreadMessages > span", {
                    opacity: 1,
                }),
                $(" .divider", {
                    width: "1px",
                    height: "24px",
                    backgroundColor: theme.palette.darkGrey,
                    margin: "0 8px",
                }),
                $(" .nuorderIconButton", {
                    height: "60px !important",
                    width: "68px !important",
                    paddingLeft: "18px !important",
                    marginRight: "4px !important",
                }),
                $(" .nuorderIconButton svg", {
                    height: "40px !important",
                    width: "40px !important",
                }),
                // $( " .menuIconButton", {
                //     paddingLeft: "18px !important",
                //     display: "none !important",
                // }),
                $(" .icon", {
                    width: "24px !important",
                    height: "24px !important",
                    marginTop: "0px !important",
                    color: "#ebebeb !important",
                }),
                $(" .toggleActionMenuButton", {
                    display: "none !important",
                }),
                media(
                    "(max-width: 900px)",
                    $(" .nuorderIconButton", {
                        display: "none !important",
                    }),
                    $(" .assortmentTitleContainer", {
                        paddingLeft: "8px",
                        maxWidth: "180px",
                    }),
                    $(" .cartButton", {
                        display: "none !important",
                    }),
                    $(" .rightPanel .cartDivider", {
                        display: "none",
                    })
                ),
                media(
                    "(max-width: 780px)",
                    $(" .rightPanel", {
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        zIndex: 6,
                        backgroundColor: isRollupMode ? theme.palette.rollupHeaderBackgound : theme.palette.darkestGrey,
                    }),
                    $(" .assortmentTitleContainer.rollupMode", {
                        maxWidth: "320px",
                    }),
                    $(" .rightPanel.notOpen > *:not(.toggleActionMenuButton)", {
                        display: "none !important",
                    }),
                    $(" .rightPanel.isOpen", {
                        left: 0,
                    }),
                    $(" .toggleActionMenuButton", {
                        display: "inline-block !important",
                    }),
                    $(" .menuIconButton", {
                        display: "inline-block !important",
                    })
                ),
                media(
                    "(max-width: 600px)",
                    $(" .scope", {
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 5,
                    }),
                    $(" .rightPanel.notOpen > *:not(.toggleActionMenuButton)", {
                        display: "none !important",
                    }),
                    $(" .rightPanel.isOpen", {
                        left: 0,
                    }),
                    $(" .toggleActionMenuButton", {
                        display: "block",
                    }),
                    $(" .menuIconButton", {
                        display: "inline-block !important",
                    }),
                    $(" .assortmentTitleContainer.rollupMode", {
                        maxWidth: "250px",
                    })
                )
            ),
        [isRollupMode]
    );

    const userStyles = React.useMemo(
        () =>
            merge(
                {
                    padding: "8px 12px 0 !important",
                    height: "60px",
                },
                $(".hasAddButton", {
                    padding: "8px 0 0 !important",
                }),
                $(" > *", {
                    display: "inline-block !important",
                }),
                $(" .iconButton", {
                    width: "40px !important",
                    height: "40px !important",
                    borderRadius: "50%",
                    backgroundColor: `${theme.palette.darkGrey} !important`,
                    border: `2px solid ${
                        isRollupMode ? theme.palette.rollupHeaderBackgound : theme.palette.darkestGrey
                    } !important`,
                    zIndex: 10,
                    padding: "0 !important",
                    margin: "0 4px 0 0 !important",
                })
            ),
        [isRollupMode]
    );

    const menuStyles = React.useMemo(
        () =>
            merge(
                $(" .profileMenuItem", {
                    fontSize: "14px !important",
                })
            ),
        []
    );

    const handleLogout = React.useCallback(() => {
        localStorage.removeItem(SearchSettings.SearchType);
        me.logout();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggleChat = React.useCallback(() => {
        onToggleChat();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [assortmentName, setAssortmentName] = React.useState(
        isRollupMode && rollupType !== RollupType.Server
            ? collection.getIn(["name", "string_value"])
            : nameExtRemover(collectionDriveItem?.name, collectionDriveItem.kind as ItemKind)
    );

    const prevIsCollectionSettingsOpen = usePrevious(isCollectionSettingsOpen);
    React.useEffect(() => {
        if (
            assortmentSettings &&
            assortmentName !== collectionDriveItem?.name &&
            !isCollectionSettingsOpen &&
            (prevIsCollectionSettingsOpen || (isRollupMode && rollupType === RollupType.Server))
        ) {
            setAssortmentName(nameExtRemover(collectionDriveItem?.name, collectionDriveItem.kind as ItemKind));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        assortmentName,
        collectionDriveItem.kind,
        collectionDriveItem?.name,
        isNew,
        assortmentSettings,
        isCollectionSettingsOpen,
        isRollupMode,
        rollupType,
    ]);

    const [previousAssortmentName, setPreviousAssortmentName] = React.useState(
        nameExtRemover(collectionDriveItem?.name, ItemKind.CollectionDocument)
    );
    const [escapeKeyPressed, setEscapeKeyPressed] = React.useState(false);
    const [tooltipVisibility, setTooltipVisibility] = React.useState(false);
    const handleKeyDown = React.useCallback(async (e) => {
        if (e.keyCode === 27) {
            // escape
            await setEscapeKeyPressed(true);
            titleRef.current.blur();
        } else if (e.keyCode === 13) {
            // enter
            titleRef.current.blur();
        }
    }, []);

    const handleEnter = React.useCallback(() => {
        setTooltipVisibility(true);
    }, []);

    const handleLeave = React.useCallback(() => {
        setTooltipVisibility(false);
    }, []);

    const handleChange = React.useCallback((e) => {
        const { value } = e.target;
        if (value.length <= MAX_LENGTH) {
            setAssortmentName(value);
        }
    }, []);

    const handleNameChange = React.useCallback(
        (e) => {
            if (escapeKeyPressed) {
                setAssortmentName(previousAssortmentName);
                setEscapeKeyPressed(false);
            } else {
                const name = e.target.value.trim();
                const valueObject = fromJS({
                    string_value: name,
                    renameOnError: () => {
                        setAssortmentName(previousAssortmentName);
                    },
                    renameOnSuccess: () => {
                        setPreviousAssortmentName(name);
                        setAssortmentName(name);
                    },
                });
                onUpdateCollectionAttribute(collectionId, "name", valueObject);
            }
        },
        [collectionId, onUpdateCollectionAttribute, escapeKeyPressed, previousAssortmentName]
    );

    React.useEffect(() => {
        if (isNew && !assortmentSettings) {
            titleRef.current.select();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { newMessages } = chatState;
    const unownedMessages = newMessages.filter((msg) => msg.identity !== me._id);
    const unseenMessages = unownedMessages.length > 9 ? "9+" : unownedMessages.length;
    const [isActionMenuOpen, setIsActionMenuOpen] = React.useState(false);
    const handleToggleActionMenu = React.useCallback(() => {
        setIsActionMenuOpen((state) => !state);
    }, []);

    const activeUsers = React.useMemo(() => {
        if (activeUserSessions) {
            return activeUserSessions
                .map((value, key) => {
                    const name = value.getIn(["sessions", 0, "identity_info", "display_name"]) || "";
                    return <Avatar key={key} label={avatarInitials(name)} color={value.get("color")} name={name} />;
                })
                .toArray();
        }
        return [];
    }, [activeUserSessions]);

    const titleRef = React.useRef<HTMLInputElement>();

    const handleCancelPoiSelection = React.useCallback(() => {
        const { query } = location;
        router.push({
            pathname: location.pathname,
            query: {
                ...query,
                poi: undefined,
                cartView: View.Grid,
            },
        });
        onSubscribeToItemsUpdates();
    }, [location, router]); // eslint-disable-line react-hooks/exhaustive-deps

    const brandIds = React.useMemo(() => Object.keys(brandsDataById), [brandsDataById]);

    // const inCart = React.useMemo(() => {
    //     return location.query.mode === "cart";
    // }, [location]);

    const handleGotoCart = React.useCallback(() => {
        router.push({
            pathname: location.pathname,
            query: {
                ...location.query,
                mode: "cart",
            },
        });
    }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

    const profileRef = React.useRef<HTMLDivElement>();
    const [profileMenuOpen, setProfileMenuOpen] = React.useState(false);
    const handleOpenProfileMenu = React.useCallback(() => setProfileMenuOpen(true), []);
    const handleCloseProfileMenu = React.useCallback(() => setProfileMenuOpen(false), []);

    let historyHeader;

    if (location.query.cartView === "history") {
        let entityTitle = formatMessage("Viewing current version");

        if (location.query && location.query.poi && poiEntities) {
            const entity = poiEntities[location.query.poi];
            entityTitle =
                entity &&
                (entity.name ||
                    `${formatMessage("Viewing version from")} ${moment(entity.created_at_ms)
                        .local()
                        .format("MMM D, h:mm A")}`);
        }

        historyHeader = (
            <div data-test={DATATEST.ASSORTMENTS.HISTORY_HEADER} className="historyHeader">
                <IconButton
                    data-test={DATATEST.ASSORTMENTS.HISTORY_EXIT}
                    onClick={handleCancelPoiSelection}
                    touch={true}
                    tooltipPosition="bottom-right"
                >
                    <BackIcon color={theme.palette.darkGrey} />
                </IconButton>
                <div className="description">
                    <div className="title">{entityTitle}</div>
                    <div>{formatMessage("Please note, assortment is not editable while viewing history")}</div>
                </div>
                {!isRollupMode && (
                    <div className="scopeContainer">
                        <div className="scopeInnerContainer">
                            <Scope
                                me={me}
                                params={params}
                                location={location}
                                router={router}
                                brandsDataById={brandsDataById}
                                brandIds={brandIds}
                                selectedBrand={collection.getIn([
                                    "brand_collection_properties",
                                    "struct_value",
                                    "brand_id",
                                ])}
                                onFetchBrandsData={onFetchBrandsData}
                                isFetchingBrands={isFetchingBrands}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const isBrandCollectionType =
        collectionDriveItem?.collection_facet?.collection_type === CollectionType.BrandStandard;
    const displayGenericBreadcrumb = isRollupMode && rollupType !== RollupType.Server;
    const userCanShare = capabilities.can_share;
    const isRenameAssortmentDisabled = React.useMemo(
        () =>
            isRollupMode || (me?.isBrandUser() && !isBrandCollectionType && !isVerticalAssortmentEnabled)
                ? true
                : !capabilities.can_rename,
        [capabilities.can_rename, isBrandCollectionType, isRollupMode, isVerticalAssortmentEnabled, me]
    );

    return (
        <div data-test={DATATEST.ASSORTMENTS.ASSORTMENT_HEADER} className="assortmentHeader" {...styles}>
            {historyHeader}
            <div className="flexItemWide">
                <IconButton className="iconButton nuorderIconButton" href={DRIVE_LINK}>
                    <NuOrderIcon className="icon" />
                </IconButton>
                <div className={classNames("assortmentTitleContainer", { rollupMode: isRollupMode })}>
                    {displayGenericBreadcrumb && (
                        <div className="assortmentTitleBreadcrumbs">
                            <a data-test={DATATEST.ASSORTMENTS.ASSORTMENTS_PAGE_LINK} href={DRIVE_LINK}>
                                {formatMessage("Assortments")}
                            </a>
                            <span>/</span>
                        </div>
                    )}
                    {!displayGenericBreadcrumb && (
                        <div className="assortmentBreadcrumbsWrapper">
                            <AssortmentBreadcrumb
                                item={collectionDriveItem}
                                merchantId={userMerchantId}
                                router={router}
                            />
                        </div>
                    )}
                    <Tooltip
                        open={isRollupMode && tooltipVisibility ? true : false}
                        title={<div style={{ fontSize: "11px", lineHeight: "18px" }}>{assortmentName}</div>}
                        placement="bottom-start"
                        {...(isRollupMode ? { onMouseEnter: handleEnter, onMouseOut: handleLeave } : {})}
                    >
                        <div>
                            <input
                                className="assortmentTitle"
                                type="text"
                                placeholder={formatMessage("Type to change name")}
                                value={assortmentName}
                                data-test={DATATEST.ASSORTMENTS.TITLE}
                                ref={titleRef}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onBlur={handleNameChange}
                                name="assortment-title"
                                disabled={isRenameAssortmentDisabled} // accroding to NU-8578, external brand user can not rename
                            />
                        </div>
                    </Tooltip>
                </div>

                {!!versionStatusLabel && (
                    <ProductCollectionStatusPill className="assortmentStatus">
                        {versionStatusLabel}
                    </ProductCollectionStatusPill>
                )}
                {!isRollupMode && (
                    <Scope
                        me={me}
                        params={params}
                        location={location}
                        router={router}
                        brandsDataById={brandsDataById}
                        brandIds={brandIds}
                        selectedBrand={collection.getIn(["brand_collection_properties", "struct_value", "brand_id"])}
                        onFetchBrandsData={onFetchBrandsData}
                        isFetchingBrands={isFetchingBrands}
                    />
                )}
            </div>
            <div className={classNames("rightPanel", { notOpen: !isActionMenuOpen, isOpen: isActionMenuOpen })}>
                <div {...userStyles} className={classNames({ hasAddButton: userCanShare })}>
                    {activeUsers}
                    {userCanShare && (
                        <IconButton
                            className="iconButton"
                            tooltip={isRollupMode ? formatMessage("Share Rollup") : formatMessage("Share Assortment")}
                            onClick={onToggleShare}
                            data-test={DATATEST.ASSORTMENTS.SHARE_BTN}
                        >
                            <ShareIcon className="icon" />
                        </IconButton>
                    )}
                </div>
                {!isRollupMode && (
                    <Badge
                        data-test={
                            unownedMessages.length > 0
                                ? DATATEST.ASSORTMENTS.CHAT_UNREAD
                                : DATATEST.ASSORTMENTS.CHAT_READ
                        }
                        className={classNames("chatBadge", { hasUnreadMessages: unownedMessages.length > 0 })}
                        primary={true}
                        badgeContent={unseenMessages}
                    >
                        <IconButton
                            data-test={DATATEST.ASSORTMENTS.CHAT_BTN}
                            tooltip={formatMessage("Chat")}
                            onClick={handleToggleChat}
                        >
                            <ChatIcon
                                stroke={showChat ? "rgba(255,255,255,.8)" : "rgba(255,255,255,.8)"}
                                fill={showChat ? "none" : "none"}
                            />
                        </IconButton>
                    </Badge>
                )}
                {((isRollupMode && userCanShare) || !isRollupMode) && <div className="divider" />}
                <div className="profile" ref={profileRef}>
                    <FlatButton
                        data-test={DATATEST.PROFILE.PROFILE_DROPDOWN}
                        className="profileButton"
                        label={me._firstName}
                        labelPosition="before"
                        onClick={handleOpenProfileMenu}
                        icon={<Caret />}
                    />
                    <Popover
                        anchorEl={profileRef.current}
                        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                        animation={PopoverAnimationVertical}
                        canAutoPosition={true}
                        onRequestClose={handleCloseProfileMenu}
                        open={profileMenuOpen}
                        targetOrigin={{ horizontal: "left", vertical: "top" }}
                    >
                        <div {...menuStyles}>
                            <Link to="/profile">
                                <MenuItem primaryText={formatMessage("Your Profile")} className="profileMenuItem" />
                            </Link>
                            <MenuItem
                                href={retailKnowledgeBaseUrl}
                                target="_blank"
                                primaryText={formatMessage("Help")}
                                className="profileMenuItem"
                            />
                            <Link to="/brands">
                                <MenuItem
                                    primaryText={formatMessage("Switch to Brand Portals")}
                                    className="profileMenuItem"
                                />
                            </Link>

                            {me.access_merchants && (
                                <Link to={`/merchant/${me.merchant_connections[0]?.merchant_id}/settings`}>
                                    <MenuItem primaryText={formatMessage("Settings")} className="profileMenuItem" />
                                </Link>
                            )}
                            <Divider />
                            <MenuItem
                                data-test={DATATEST.PROFILE.PROFILE_LOGOUT}
                                primaryText={formatMessage("Sign Out")}
                                onClick={handleLogout}
                                className="profileMenuItem"
                            />
                        </div>
                    </Popover>
                </div>
                {!isRollupMode && (
                    <>
                        <div className="divider cartDivider" />
                        <IconButton
                            data-test={DATATEST.ASSORTMENTS.CART_BTN}
                            onClick={handleGotoCart}
                            className="cartButton"
                        >
                            <CartIcon fillInBasket={false} />
                        </IconButton>
                    </>
                )}

                <IconButton className="iconButton toggleActionMenuButton" onClick={handleToggleActionMenu}>
                    {isActionMenuOpen ? <CloseIcon className="icon" /> : <MoreIcon className="icon" />}
                </IconButton>
            </div>
        </div>
    );
};
